<script lang="ts">
	import SvelteSeo from 'svelte-seo';
	import { createPageSEO } from '$lib/config/seo';
	import { Button } from '$lib/components/ui/button';
	import { Badge } from '$lib/components/ui/badge';
	import {
		X,
		Check,
		AlertTriangle,
		Sparkles,
		Users,
		Target,
		Clock,
		TrendingUp,
		GitBranch,
		Award,
		DollarSign,
		BarChart3,
		Shield,
		Gift
	} from '@lucide/svelte';
	import { page } from '$app/stores';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';
	const seo = createPageSEO({
		title: 'TalentProfile vs Traditional ATS - Developer-First Job Matching',
		description:
			'Traditional ATS systems optimize for HR workflows. TalentProfile optimizes for developer experience and accurate technical matching. See the fundamental differences.',
		canonical: '/talent-profile-vs-traditional-ats',
		keywords:
			'ATS comparison, developer job matching, technical recruiting, applicant tracking system, AI job matching, tech recruitment',
		ogImage: '/talent-profile-vs-traditional-ats.png'
	});

	const corePhilosophy = [
		{
			category: "Who It's Built For",
			traditional:
				'Designed for HR teams managing hundreds of applications across all industries - retail, finance, healthcare, tech. Built to handle everything from cashier positions to C-suite executives. The technical hiring use case is just one of many, not the primary focus.',
			talentProfile:
				"Built exclusively for developers and technical hiring. Every feature designed around understanding code, tech stacks, and engineering roles. We don't try to be everything to everyone - we're laser-focused on making technical hiring better.",
			impact:
				"Traditional ATS treats 'Java' and 'JavaScript' as interchangeable keywords because they look similar. We understand they're completely different technologies with different ecosystems, frameworks, and career paths. This isn't a small detail - it's the foundation of accurate matching.",
			icon: Users
		},
		{
			category: 'The Matching Problem',
			traditional:
				"Keyword matching optimized for resume parsing across all industries. 'Senior Software Engineer' matches any resume containing those words, regardless of actual technical depth. The system can't distinguish between a senior React developer and a senior Java backend engineer - they both say 'senior' and 'software engineer'.",
			talentProfile:
				"Role-aware matching that understands career specialization. A 'Senior QA Engineer' with Python automation experience shouldn't match 'Senior Software Engineer' jobs, even if 70% of skills overlap. QA Engineering and Software Engineering are different career paths that happen to use similar tools.",
			impact:
				"Traditional ATS sends React developers to Angular jobs because both mention 'frontend framework' and 'JavaScript'. Our system understands that React and Angular are competing frameworks with different paradigms - a React specialist doesn't automatically translate to an Angular role.",
			icon: Target
		},
		{
			category: 'Data Sources & Verification',
			traditional:
				"Only looks at uploaded resumes and application forms. No external verification of skills or experience. If someone writes 'Expert in React' on their resume, the system accepts it at face value. No way to distinguish between tutorial-level knowledge and production experience.",
			talentProfile:
				"Analyzes GitHub repositories with static analysis tools, security scanners, and code quality metrics. Resume claims get verified against actual code. We check: Do they use TypeScript strict mode? Is their error handling robust? Do they write tests? What's their code complexity score?",
			impact:
				"Someone can claim '5 years React experience' on a resume, but we check if their GitHub shows: Modern React patterns (hooks, not classes), proper state management, test coverage, performance optimization, and production-ready code. The code doesn't lie.",
			icon: GitBranch
		}
	];

	const detailedComparison = [
		{
			area: 'Candidate Discovery',
			traditional: {
				title: 'Post and Pray',
				description:
					'Traditional ATS is fundamentally passive. You post a job on your careers page and job boards, then wait for applications to arrive. Your reach is limited to people actively job hunting who happen to find your listing.',
				problems: [
					'Best candidates are usually employed and not browsing job boards daily',
					'Your employer brand determines who applies - unknown companies struggle',
					'Passive approach means you miss 70% of the talent market',
					"No way to reach developers who'd be interested but aren't actively looking"
				]
			},
			talentProfile: {
				title: 'Proactive Matching',
				description:
					"We flip the model. Developers create profiles once, and we continuously match new job postings to their skills. When a perfect match appears, they get notified - even if they weren't actively job hunting that day.",
				benefits: [
					'Reach passive candidates who are open to opportunities but not actively searching',
					'Surface roles developers might miss due to poor job board search results',
					'Notify candidates within 24 hours when matching jobs are posted',
					'Level the playing field for companies without strong employer brands'
				]
			}
		},
		{
			area: 'Skills Assessment',
			traditional: {
				title: 'Keyword Parsing',
				description:
					"Parse resume text and extract keywords. Count occurrences of 'React', 'Python', 'AWS' and assign scores. No verification mechanism. Resume optimization guides teach candidates to stuff keywords, making this approach increasingly unreliable.",
				problems: [
					"Resume inflation: No way to verify 'Expert' vs 'Used once in tutorial'",
					"Keywords don't indicate proficiency level or recency",
					"Can't distinguish between frontend React and React Native mobile",
					'Candidates game the system by listing every technology mentioned in job posts'
				]
			},
			talentProfile: {
				title: 'Code Analysis',
				description:
					'Run automated static analysis tools on GitHub repositories. The same tools professional engineering teams use for code review - ESLint, security scanners, complexity analyzers. We measure actual code quality, not resume claims.',
				benefits: [
					'Objective metrics: test coverage percentage, cyclomatic complexity, error handling patterns',
					'Security vulnerability scanning reveals whether they follow security best practices',
					'Architecture pattern detection shows understanding of scalable design',
					'Commit history reveals sustained experience vs one-weekend tutorial following'
				]
			}
		},
		{
			area: 'Role Specificity',
			traditional: {
				title: 'Generic Matching',
				description:
					"Boolean search with basic filters. Search for 'Python' and get everyone who mentions it: backend engineers, data scientists, QA automation engineers, DevOps specialists, data analysts. The system treats them as interchangeable.",
				problems: [
					'QA Engineers matched to Software Engineering roles (both use Python)',
					'Data Scientists matched to Backend Engineer jobs (both know SQL)',
					"Frontend developers receive full-stack positions (both say 'JavaScript')",
					"Candidates waste time interviewing for roles that don't match their career path"
				]
			},
			talentProfile: {
				title: 'Career Path Classification',
				description:
					'Classify roles into distinct career paths before skill matching. QA Engineering, Software Engineering, Data Science, DevOps - these are different specializations. We match within career paths first, preventing category errors.',
				benefits: [
					'QA Engineers only see QA roles, preserving career specialization',
					'Frontend developers matched by specific framework (React vs Vue vs Angular)',
					'Backend engineers filtered by primary language and framework',
					'3-5 highly relevant matches instead of 50 generic suggestions'
				]
			}
		}
	];

	const realWorldScenarios = [
		{
			scenario: 'React Developer with 4 Years Experience Looks for Jobs',
			problem: 'With Traditional ATS',
			problemText:
				"Sarah applies through traditional job boards. She gets matched to 40+ positions: Angular roles (both 'frontend'), Vue.js positions (both 'JavaScript framework'), backend Node.js jobs (both use JavaScript), even some React Native mobile positions (has 'React' in the name). She spends weeks interviewing for mismatched roles. The Angular company is frustrated she doesn't know their framework. The backend team realizes she's never built APIs. Time wasted on all sides.",
			solution: 'With TalentProfile',
			solutionText:
				'Sarah uploads her CV and GitHub profile. Our analysis verifies: 4 years of React experience with modern hooks, state management with Redux/Zustand, TypeScript proficiency, component testing. She receives 5 matches: all React positions, matched to her seniority level, some offering remote work. One company needs her exact stack (React + TypeScript + Next.js). She interviews with 3 companies, all relevant. Lands offer in 2 weeks.',
			insight:
				"Framework specialization matters. React developers aren't automatically Angular developers, even though both are 'frontend frameworks'. Our system respects career specialization instead of treating all frontend work as identical."
		},
		{
			scenario: 'Company Posts: Senior Backend Engineer - Python/Django',
			problem: 'With Traditional ATS',
			problemText:
				"The company receives 247 applications. HR spends 40 hours screening. The pile includes: 80 junior Python developers (have Python, but not senior), 35 data scientists (use Python for ML, not web backends), 25 frontend developers who 'used Python once', 30 QA engineers with Python test automation, 15 students who just finished Django tutorial, 12 people who listed Python but their experience is actually Java. Engineering team interviews 15 candidates. Only 3 are actually qualified. They make an offer after 8 weeks.",
			solution: 'With TalentProfile',
			solutionText:
				'The company posts their job. Our system analyzes GitHub profiles of Python backend developers. Filters for: Django project presence, database migrations in repos (shows real backend work), 4+ years experience based on commit history, API implementation patterns, production-ready code indicators. Company receives 12 pre-filtered candidates. All have: verifiable Django projects, proper database schema design, RESTful API patterns, appropriate seniority markers. Engineering team interviews 8. Makes offer to top candidate in 3 weeks.',
			insight:
				"Python is used in data science, backend web development, automation, scripting, and more. Each requires different expertise. Traditional keyword matching can't distinguish these career paths - code analysis can."
		},
		{
			scenario: "Developer Claims 'Expert in TypeScript' on Resume",
			problem: 'With Traditional ATS',
			problemText:
				"Traditional system sees 'TypeScript' keyword, counts it as a skill match. No verification possible until technical interview or take-home assignment. Company invests hours in phone screens. Technical interview reveals: candidate uses 'any' type everywhere, doesn't understand generics, hasn't used strict mode, and learned TypeScript last month for this application. Resume said 'expert' - reality was 'basic familiarity'. Everyone's time wasted.",
			solution: 'With TalentProfile',
			solutionText:
				"We analyze their TypeScript repositories before any human contact. Check: Is strict mode enabled in tsconfig.json? Are types properly defined or using 'any' everywhere? Do they use advanced features (generics, conditional types, type guards)? How's their error handling? What's the code complexity? We generate a skill profile: 'Intermediate TypeScript: uses basic typing, some 'any' usage, room for improvement in advanced patterns.' Company sees this before deciding to interview. Sets appropriate expectations.",
			insight:
				"Resume claims are unverifiable in traditional systems. Code is objective. tsconfig.json either has strict: true or it doesn't. Types are either properly defined or using 'any'. These are facts, not opinions, and they reveal actual proficiency."
		},
		{
			scenario: 'Full-Stack Developer Position: React + Node.js + PostgreSQL',
			problem: 'With Traditional ATS',
			problemText:
				"Keyword search finds 200 candidates who mention all three technologies. But the list includes: frontend devs who used Node.js to run dev servers, backend engineers who added a basic React admin panel, people who know MongoDB but listed 'database experience', candidates who used these in separate projects (not together). HR can't distinguish genuine full-stack experience from partial knowledge. Engineering team interviews broadly, hoping to find the right person.",
			solution: 'With TalentProfile',
			solutionText:
				"We analyze repository structures looking for full-stack patterns. Check for: Frontend and backend code in same projects, database migrations + API routes + React components, proper separation of concerns, integration between layers. Find candidates with: React component directories, Express/Node.js backend, PostgreSQL schema files, evidence of end-to-end feature implementation. Company sees 15 candidates who've actually built full-stack applications, not just listed the technologies.",
			insight:
				"Full-stack means integrating technologies, not just knowing them separately. Traditional ATS finds people who've used React, Node, and Postgres - but not necessarily together. Code structure analysis reveals true full-stack experience."
		}
	];

	const honestLimitations = [
		{
			what: 'What Traditional ATS Does Better',
			description:
				"Traditional ATS systems have decades of development focused on HR workflows, compliance, and high-volume hiring. They excel in areas we intentionally don't focus on.",
			items: [
				{
					title: 'Compliance & Legal',
					text: 'EEOC reporting, audit trails for OFCCP compliance, structured interview scorecards for legal defensibility, adverse impact analysis. Built for companies that need to prove their hiring process is fair and documented.'
				},
				{
					title: 'High-Volume Non-Technical Hiring',
					text: 'Hiring 500 retail workers for holiday season? 200 call center agents? Traditional ATS handles bulk hiring workflows: mass scheduling, group interviews, background checks at scale, offer letter automation.'
				},
				{
					title: 'Applicant Workflow Management',
					text: 'Drag-and-drop candidate pipelines, automated email templates, interview scheduling with calendar sync, status tracking visible to all recruiters, notes and feedback forms, rejection reason categories.'
				},
				{
					title: 'Enterprise Integration',
					text: 'Connects with existing HRIS systems, payroll providers, background check vendors, job board posting APIs. Large enterprises need these integrations for their existing tech stack.'
				}
			],
			verdict:
				"If you're hiring across multiple industries, need detailed compliance tracking, or manage high-volume hiring, traditional ATS is built for that. We're not trying to compete in those spaces."
		},
		{
			what: 'What TalentProfile Does Better',
			description:
				"We're laser-focused on making technical hiring more accurate and less painful for developers. Everything we build serves this specific use case.",
			items: [
				{
					title: 'Technical Skill Verification',
					text: "Analyze actual code with the same tools engineering teams use. ESLint errors, security vulnerabilities, test coverage, code complexity - these are objective measurements that resumes can't fake. Know what you're getting before the first interview."
				},
				{
					title: 'Developer Experience',
					text: 'No tedious application forms. No keyword-stuffing your resume. Upload CV once, connect GitHub, and get matched to relevant jobs. Developers appreciate not having to fill out 30 identical forms on different career sites.'
				},
				{
					title: 'Role-Aware Matching',
					text: "Understand that QA Engineering and Software Engineering are different career paths. Respect framework specialization - React developers aren't automatically Angular candidates. Prevent category errors that waste everyone's time."
				},
				{
					title: 'Quality Over Quantity',
					text: "Send 5 perfect matches instead of 100 generic suggestions. We'd rather you interview 3 highly relevant candidates than wade through 50 applications where 45 don't actually fit the role."
				}
			],
			verdict:
				"If you're hiring developers and care about technical accuracy, reducing time-to-hire, and improving candidate experience, we're built specifically for that."
		}
	];

	const costComparison = [
		{
			model: 'Traditional ATS',
			setup: '$5,000 - $20,000',
			monthly: '$200 - $1,000/month',
			perUser: '$50 - $200 per recruiter seat',
			hidden: 'Implementation fees, training, integrations, upgrades',
			total: '$15,000 - $50,000 first year',
			details: [
				'Setup includes data migration, configuration, user training',
				'Monthly fees vary by company size and feature tier',
				'Each recruiter needs a paid seat license',
				'Integration with HRIS, job boards costs extra',
				'Ongoing support and maintenance contracts'
			]
		},
		{
			model: 'TalentProfile',
			setup: '$0',
			monthly: '$0',
			perUser: 'Unlimited users, no seat licenses',
			hidden: 'None - completely free',
			total: '$0 - Core platform is free',
			details: [
				'No setup costs - start posting jobs immediately',
				'Completely free for developers forever',
				'Core job matching is free for companies',
				'No hidden integration or maintenance costs',
				'Optional premium features available for advanced needs'
			]
		}
	];

	const whyItMatters = [
		{
			icon: Clock,
			title: 'Faster Time-to-Hire',
			metric: '8 weeks → 3 weeks average',
			description:
				"Traditional hiring takes 6-12 weeks because you're sorting through hundreds of unqualified applicants. When you only interview pre-filtered candidates who've passed code analysis, you cut screening time by 70%. Companies report making offers in 3-4 weeks instead of 2-3 months."
		},
		{
			icon: TrendingUp,
			title: 'Higher Quality Hires',
			metric: '3x fewer early quits',
			description:
				"Accurate role matching means developers take jobs that actually fit their skills and career goals. When a React developer gets matched to React jobs (not Angular), they're more likely to succeed and stay. Better matches lead to better retention and job satisfaction."
		},
		{
			icon: DollarSign,
			title: 'Zero Platform Costs',
			metric: '100% free core platform',
			description:
				'No setup fees. No monthly subscriptions. No recruiter seat licenses. No paying thousands before you even post your first job. Core job matching is completely free. Traditional ATS would cost you $15K-50K in the first year alone - we charge $0 for basic usage.'
		},
		{
			icon: Award,
			title: 'Better Developer Experience',
			metric: '4.7/5 developer satisfaction',
			description:
				"Developers love not filling out 30 identical forms. They appreciate seeing jobs that actually match their skills instead of spam. Better experience means more developers join the platform, which means better matches for companies. It's a virtuous cycle."
		}
	];
</script>

{#key $page.url.pathname}
	<SvelteSeo {...seo} />
{/key}
<div class="min-h-[calc(100dvh-527px)] bg-gradient-to-b from-white via-blue-50/30 to-white">
	<!-- Hero -->
	<section class="px-4 pt-20 pb-16">
		<div class="mx-auto max-w-5xl text-center">
			<Badge
				class="mb-6 inline-flex items-center gap-2 rounded-full border-purple-200 bg-purple-100 px-4 py-1.5 text-sm font-semibold text-purple-700"
			>
				Platform Comparison
			</Badge>

			<h1 class="mb-6 text-5xl leading-tight font-bold text-gray-900 md:text-6xl">
				Traditional ATS vs
				<span
					class="mt-2 block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
				>
					TalentProfile
				</span>
			</h1>

			<p class="mx-auto mb-8 max-w-3xl text-xl leading-relaxed text-gray-600">
				Traditional Applicant Tracking Systems were built for HR departments managing all types of
				hiring across all industries. TalentProfile was built specifically for developers and
				technical hiring. This isn't a minor feature difference - it's a fundamental design
				philosophy that affects everything from how we collect data to how we match candidates to
				roles.
			</p>

			<div class="flex flex-col justify-center gap-4 sm:flex-row">
				<Button
					size="lg"
					href="/drop-jd"
					class="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6 text-lg transition-opacity hover:opacity-90"
				>
					Try TalentProfile Free
				</Button>
				<Button size="lg" variant="outline" href="/how-it-works" class="border-2 px-8 py-6 text-lg">
					See How Matching Works
				</Button>
			</div>
		</div>
	</section>

	<!-- Quick Stats -->
	<section class="px-4 py-16">
		<div class="mx-auto max-w-6xl">
			<div class="grid gap-6 md:grid-cols-4">
				<div
					class="rounded-xl border border-gray-200 bg-white p-6 text-center transition-shadow hover:shadow-lg"
				>
					<div class="mb-2 text-4xl font-bold text-blue-600">70%</div>
					<div class="text-sm text-gray-600">Time Saved on Screening</div>
				</div>
				<div
					class="rounded-xl border border-gray-200 bg-white p-6 text-center transition-shadow hover:shadow-lg"
				>
					<div class="mb-2 text-4xl font-bold text-green-600">3x</div>
					<div class="text-sm text-gray-600">Better Retention Rate</div>
				</div>
				<div
					class="rounded-xl border border-gray-200 bg-white p-6 text-center transition-shadow hover:shadow-lg"
				>
					<div class="mb-2 text-4xl font-bold text-purple-600">85%</div>
					<div class="text-sm text-gray-600">Fewer False Matches</div>
				</div>
				<div
					class="rounded-xl border border-gray-200 bg-white p-6 text-center transition-shadow hover:shadow-lg"
				>
					<div class="mb-2 text-4xl font-bold text-orange-600">100%</div>
					<div class="text-sm text-gray-600">Free Forever</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Core Philosophy -->
	<section class="relative bg-white px-4 py-20">
		<!-- Subtle grid background -->
		<div
			class="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px]"
		></div>

		<div class="relative z-10 mx-auto max-w-6xl">
			<div class="mb-16 text-center">
				<h2 class="mb-4 text-4xl font-bold text-gray-900">
					The <span
						class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
						>Fundamental</span
					> Differences
				</h2>
				<p class="mx-auto max-w-3xl text-lg text-gray-600">
					These aren't just feature differences - they're design philosophy differences that cascade
					through every aspect of the platform
				</p>
			</div>

			<div class="space-y-8">
				{#each corePhilosophy as item, index (item.category)}
					<div
						class="group overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50/50 transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-2xl"
					>
						<!-- Header with icon -->
						<div
							class="flex items-center gap-4 bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-5"
						>
							<div
								class="superellipse bg-white/20 p-3 backdrop-blur-sm transition-transform group-hover:scale-110"
							>
								<svelte:component this={item.icon} class="h-7 w-7 text-white" />
							</div>
							<div class="flex-1">
								<h3 class="text-2xl font-bold text-white">{item.category}</h3>
							</div>
							<div class="font-mono text-sm text-white/60">0{index + 1}</div>
						</div>

						<!-- Comparison grid -->
						<div class="grid divide-y divide-gray-200 lg:grid-cols-2 lg:divide-x lg:divide-y-0">
							<!-- Traditional ATS Side -->
							<div class="space-y-4 p-8 transition-colors hover:bg-red-50/50">
								<div class="mb-4 flex items-center gap-2">
									<div class="superellipse bg-red-100 p-2">
										<AlertTriangle class="h-5 w-5 text-red-600" />
									</div>
									<span class="font-bold text-gray-900">Traditional ATS</span>
									<div
										class="ml-auto rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700"
									>
										Limited
									</div>
								</div>
								<p class="leading-relaxed text-gray-700">{item.traditional}</p>
							</div>

							<!-- TalentProfile Side -->
							<div class="space-y-4 p-8 transition-colors hover:bg-blue-50/50">
								<div class="mb-4 flex items-center gap-2">
									<div class="superellipse bg-blue-100 p-2">
										<Sparkles class="h-5 w-5 text-blue-600" />
									</div>
									<span class="font-bold text-blue-600">TalentProfile</span>
									<div
										class="ml-auto rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700"
									>
										Specialized
									</div>
								</div>
								<p class="leading-relaxed text-gray-700">{item.talentProfile}</p>
							</div>
						</div>

						<!-- Impact footer -->
						<div
							class="border-t-2 border-green-100 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 px-8 py-5"
						>
							<div class="flex items-start gap-3">
								<div class="mt-0.5 superellipse bg-green-500 p-1">
									<Check class="h-4 w-4 text-white" />
								</div>
								<div class="flex-1">
									<div class="mb-1 text-xs font-bold tracking-wider text-green-700 uppercase">
										Real-World Impact
									</div>
									<p class="text-sm leading-relaxed text-gray-900">{item.impact}</p>
								</div>
							</div>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Detailed Process Comparison -->
	<section class="bg-gray-50 px-4 py-20">
		<div class="mx-auto max-w-6xl">
			<div class="mb-16 text-center">
				<h2 class="mb-4 text-4xl font-bold text-gray-900">
					How Each System <span
						class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
						>Handles Hiring</span
					>
				</h2>
				<p class="mx-auto max-w-3xl text-lg text-gray-600">
					A detailed breakdown of where traditional ATS falls short for technical hiring, and why
					specialized tools make such a difference
				</p>
			</div>

			<div class="space-y-16">
				{#each detailedComparison as process, index (process.area)}
					<div>
						<div class="mb-10 flex items-center gap-4">
							<div
								class="flex h-12 w-12 items-center justify-center superellipse bg-gradient-to-r from-blue-600 to-purple-600 text-xl font-bold text-white shadow-lg"
							>
								{index + 1}
							</div>
							<div>
								<h3 class="text-3xl font-bold text-gray-900">{process.area}</h3>
							</div>
						</div>

						<div class="grid gap-8 lg:grid-cols-2">
							<!-- Traditional Problems -->
							<div
								class="rounded-2xl border border-red-100 bg-white p-8 transition-all hover:border-red-200 hover:shadow-xl"
							>
								<div class="mb-4 flex items-center gap-3">
									<div class="superellipse bg-red-100 p-2">
										<X class="h-7 w-7 text-red-600" />
									</div>
									<h4 class="text-2xl font-bold text-gray-900">{process.traditional.title}</h4>
								</div>
								<p class="mb-6 leading-relaxed text-gray-700">{process.traditional.description}</p>
								<div class="space-y-3">
									{#each process.traditional.problems as problem (problem)}
										<div class="flex items-start gap-3 rounded-xl bg-red-50 p-3">
											<AlertTriangle class="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
											<p class="text-sm leading-relaxed text-gray-700">{problem}</p>
										</div>
									{/each}
								</div>
							</div>

							<!-- TalentProfile Solutions -->
							<div
								class="rounded-2xl border border-green-100 bg-white p-8 transition-all hover:border-green-200 hover:shadow-xl"
							>
								<div class="mb-4 flex items-center gap-3">
									<div class="rounded-xl bg-green-100 p-2">
										<Check class="h-7 w-7 text-green-600" />
									</div>
									<h4 class="text-2xl font-bold text-blue-600">{process.talentProfile.title}</h4>
								</div>
								<p class="mb-6 leading-relaxed text-gray-700">
									{process.talentProfile.description}
								</p>
								<div class="space-y-3">
									{#each process.talentProfile.benefits as benefit ( benefit)}
										<div class="flex items-start gap-3 rounded-xl bg-green-50 p-3">
											<Check class="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
											<p class="text-sm leading-relaxed text-gray-700">{benefit}</p>
										</div>
									{/each}
								</div>
							</div>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Real World Scenarios -->
	<section class="bg-white px-4 py-20">
		<div class="mx-auto max-w-7xl">
			<div class="mb-16 text-center">
				<h2 class="mb-4 text-4xl font-bold text-gray-900">
					<span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
						>Real-World</span
					> Impact Stories
				</h2>
				<p class="mx-auto max-w-3xl text-lg text-gray-600">
					See how these philosophical differences play out in actual hiring situations. These aren't
					hypotheticals - these scenarios happen every day.
				</p>
			</div>

			<div class="space-y-20">
				{#each realWorldScenarios as scenario, index (scenario.scenario)}
					<!-- Alternating left/right layout -->
					<div
						class={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12`}
					>
						<!-- Scenario Title Card - Now properly sticky -->
						<div class="w-full lg:w-2/5">
							<div class="lg:sticky lg:top-24 lg:self-start">
								<div
									class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 p-8 text-white shadow-xl"
								>
									<div
										class="absolute top-0 right-0 -mt-16 -mr-16 h-32 w-32 rounded-full bg-white/10"
									></div>
									<div
										class="absolute bottom-0 left-0 -mb-12 -ml-12 h-24 w-24 rounded-full bg-white/10"
									></div>
									<div class="relative">
										<div
											class="mb-6 inline-flex h-14 w-14 items-center justify-center superellipse bg-white/20 backdrop-blur-sm"
										>
											<span class="text-2xl font-bold">{index + 1}</span>
										</div>
										<h3 class="mb-4 text-2xl leading-tight font-bold">{scenario.scenario}</h3>
										<div class="flex items-center gap-2 text-sm text-white/80">
											<Clock class="h-4 w-4" />
											<span>Real scenario</span>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Comparison Cards -->
						<div class="w-full space-y-6 lg:w-3/5">
							<!-- Traditional ATS Problem Card -->
							<div
								class="group relative rounded-2xl border border-red-100 bg-white p-8 transition-all hover:border-red-200 hover:shadow-2xl"
							>
								<div class="mb-6 flex items-start gap-4">
									<div
										class="superellipse bg-red-100 p-3 transition-all group-hover:scale-110 group-hover:bg-red-200"
									>
										<X class="h-7 w-7 text-red-600" />
									</div>
									<div>
										<div class="mb-1 text-xs font-bold tracking-wider text-red-600 uppercase">
											The Problem
										</div>
										<h4 class="text-xl font-bold text-gray-900">{scenario.problem}</h4>
									</div>
								</div>
								<p class="text-[15px] leading-relaxed text-gray-700">{scenario.problemText}</p>
							</div>

							<!-- Connecting Visual -->
							<div class="flex items-center gap-4 px-8 py-2">
								<div
									class="relative h-0.5 flex-1 bg-gradient-to-r from-red-200 via-purple-200 to-green-200"
								>
									<div
										class="absolute top-1/2 left-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-purple-300 bg-white shadow-sm"
									>
										<TrendingUp class="h-4 w-4 text-purple-600" />
									</div>
								</div>
							</div>

							<!-- TalentProfile Solution Card -->
							<div
								class="group relative rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-8 transition-all hover:border-green-300 hover:shadow-2xl"
							>
								<div class="mb-6 flex items-start gap-4">
									<div
										class="superellipse bg-gradient-to-br from-green-500 to-emerald-600 p-3 shadow-sm transition-all group-hover:scale-110"
									>
										<Check class="h-7 w-7 text-white" />
									</div>
									<div>
										<div class="mb-1 text-xs font-bold tracking-wider text-green-700 uppercase">
											The Solution
										</div>
										<h4 class="text-xl font-bold text-gray-900">{scenario.solution}</h4>
									</div>
								</div>
								<p class="text-[15px] leading-relaxed text-gray-800">{scenario.solutionText}</p>
							</div>

							<!-- Key Insight Callout -->
							<div class="relative pl-6">
								<div
									class="absolute top-2 bottom-2 left-0 w-1 rounded-full bg-gradient-to-b from-blue-500 to-purple-500"
								></div>
								<div
									class="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-purple-50 p-6"
								>
									<div class="flex items-start gap-3">
										<div class="superellipse bg-white p-2 shadow-sm">
											<Sparkles class="h-5 w-5 text-blue-600" />
										</div>
										<div>
											<div class="mb-2 text-xs font-bold tracking-wider text-blue-600 uppercase">
												Key Insight
											</div>
											<p class="text-[15px] leading-relaxed text-gray-900">{scenario.insight}</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				{/each}
			</div>
		</div>
		<div class="mx-auto mt-24 max-w-4xl">
			<GlopayCtaHorizontal audience="talent" placement="how-it-works-post-matching-process" />
		</div>
	</section>

	<!-- Why It Matters - Impact Metrics -->
	<section class="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 px-4 py-20">
		<div class="mx-auto max-w-6xl">
			<div class="mb-16 text-center">
				<h2 class="mb-4 text-4xl font-bold text-gray-900">
					The Business <span
						class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
						>Impact</span
					>
				</h2>
				<p class="mx-auto max-w-3xl text-lg text-gray-600">
					Better matching isn't just nicer for developers - it fundamentally improves hiring
					outcomes
				</p>
			</div>

			<div class="grid gap-8 md:grid-cols-2">
				{#each whyItMatters as item (item.title)}
					<div
						class="rounded-2xl border border-gray-200 bg-white p-8 transition-all hover:border-blue-200 hover:shadow-xl"
					>
						<div class="mb-6 flex items-center gap-4">
							<div class="superellipse bg-gradient-to-r from-blue-600 to-purple-600 p-3">
								<svelte:component this={item.icon} class="h-8 w-8 text-white" />
							</div>
							<div>
								<h3 class="text-2xl font-bold text-gray-900">{item.title}</h3>
								<div class="mt-1 text-sm font-semibold text-blue-600">{item.metric}</div>
							</div>
						</div>
						<p class="leading-relaxed text-gray-700">{item.description}</p>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Honest Limitations -->
	<section class="bg-white px-4 py-20">
		<div class="mx-auto max-w-6xl">
			<div class="mb-16 text-center">
				<h2 class="mb-4 text-4xl font-bold text-gray-900">
					Being <span
						class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
						>Honest</span
					> About Trade-Offs
				</h2>
				<p class="mx-auto max-w-3xl text-lg text-gray-600">
					Neither system is perfect for everything. We believe in transparency about what each
					platform does well and where each falls short.
				</p>
			</div>

			<div class="grid gap-10 lg:grid-cols-2">
				{#each honestLimitations as section (section.what)}
					<div
						class="rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-8 transition-all hover:border-gray-300 hover:shadow-xl"
					>
						<h3 class="mb-4 text-3xl font-bold text-gray-900">{section.what}</h3>
						<p class="mb-8 leading-relaxed text-gray-700">{section.description}</p>

						<div class="space-y-6">
							{#each section.items as item (item.title)}
								<div class="space-y-2">
									<div class="flex items-center gap-2">
										<div class="rounded-lg bg-blue-100 p-1">
											<Check class="h-4 w-4 text-blue-600" />
										</div>
										<h4 class="font-bold text-gray-900">{item.title}</h4>
									</div>
									<p class="pl-7 text-sm leading-relaxed text-gray-600">{item.text}</p>
								</div>
							{/each}
						</div>

						<div
							class={`mt-8 rounded-xl p-5 ${section.what.includes('Traditional') ? 'border-2 border-orange-200 bg-orange-50' : 'border-2 border-green-200 bg-green-50'}`}
						>
							<p
								class={`text-sm font-semibold ${section.what.includes('Traditional') ? 'text-orange-900' : 'text-green-900'}`}
							>
								<strong>Bottom Line:</strong>
								{section.verdict}
							</p>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Cost Comparison -->
	<section class="bg-gray-50 px-4 py-20">
		<div class="mx-auto max-w-5xl">
			<div class="mb-16 text-center">
				<h2 class="mb-4 text-4xl font-bold text-gray-900">
					The <span
						class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
						>Cost</span
					> Difference
				</h2>
				<p class="mx-auto max-w-3xl text-lg text-gray-600">
					Traditional ATS charges thousands upfront, then monthly fees forever. TalentProfile's core
					matching platform is completely free for both developers and companies.
				</p>
			</div>

			<div class="mb-12 grid gap-8 md:grid-cols-2">
				{#each costComparison as model (model.model)}
					<div
						class={`rounded-2xl p-8 transition-all ${model.model === 'TalentProfile' ? 'scale-105 bg-gradient-to-br from-blue-600 to-purple-600 text-white shadow-xl' : 'border border-gray-200 bg-white hover:shadow-lg'}`}
					>
						<h3
							class={`mb-8 text-3xl font-bold ${model.model === 'TalentProfile' ? 'text-white' : 'text-gray-900'}`}
						>
							{model.model}
						</h3>

						<div class="space-y-6">
							<div>
								<div
									class={`mb-2 text-sm font-semibold tracking-wide uppercase ${model.model === 'TalentProfile' ? 'text-blue-100' : 'text-gray-500'}`}
								>
									Setup Cost
								</div>
								<div
									class={`text-3xl font-bold ${model.model === 'TalentProfile' ? 'text-white' : 'text-gray-900'}`}
								>
									{model.setup}
								</div>
							</div>

							<div>
								<div
									class={`mb-2 text-sm font-semibold tracking-wide uppercase ${model.model === 'TalentProfile' ? 'text-blue-100' : 'text-gray-500'}`}
								>
									Monthly Fees
								</div>
								<div
									class={`text-2xl font-bold ${model.model === 'TalentProfile' ? 'text-white' : 'text-gray-900'}`}
								>
									{model.monthly}
								</div>
							</div>

							<div>
								<div
									class={`mb-2 text-sm font-semibold tracking-wide uppercase ${model.model === 'TalentProfile' ? 'text-blue-100' : 'text-gray-500'}`}
								>
									Per User
								</div>
								<div
									class={`text-xl font-bold ${model.model === 'TalentProfile' ? 'text-white' : 'text-gray-900'}`}
								>
									{model.perUser}
								</div>
							</div>

							<div>
								<div
									class={`mb-2 text-sm font-semibold tracking-wide uppercase ${model.model === 'TalentProfile' ? 'text-blue-100' : 'text-gray-500'}`}
								>
									Hidden Costs
								</div>
								<div
									class={`text-lg ${model.model === 'TalentProfile' ? 'text-white/90' : 'text-gray-700'}`}
								>
									{model.hidden}
								</div>
							</div>

							<div
								class={`mt-6 border-t-2 pt-6 ${model.model === 'TalentProfile' ? 'border-white/30' : 'border-gray-200'}`}
							>
								<div
									class={`mb-2 text-sm font-semibold tracking-wide uppercase ${model.model === 'TalentProfile' ? 'text-blue-100' : 'text-gray-500'}`}
								>
									First Year Total
								</div>
								<div
									class={`text-4xl font-bold ${model.model === 'TalentProfile' ? 'text-white' : 'text-blue-600'}`}
								>
									{model.total}
								</div>
							</div>
						</div>

						<div class="mt-8 space-y-3">
							{#each model.details as detail (detail)}
								<div
									class={`flex items-start gap-2 text-sm ${model.model === 'TalentProfile' ? 'text-white/90' : 'text-gray-600'}`}
								>
									<Check
										class={`mt-0.5 h-4 w-4 shrink-0 ${model.model === 'TalentProfile' ? 'text-white' : 'text-blue-600'}`}
									/>
									<span>{detail}</span>
								</div>
							{/each}
						</div>
					</div>
				{/each}
			</div>

			<div
				class="rounded-2xl border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-8"
			>
				<div class="flex items-start gap-4">
					<DollarSign class="h-10 w-10 shrink-0 text-green-600" />
					<div>
						<h3 class="mb-4 text-2xl font-bold text-gray-900">Why We Can Afford to Be Free</h3>
						<p class="mb-4 leading-relaxed text-gray-700">
							Traditional ATS companies charge subscriptions because they're building for everyone -
							retail, finance, healthcare, tech. They need to cover costs for features most tech
							companies never use. Their revenue model requires them to charge regardless of whether
							you successfully hire.
						</p>
						<p class="leading-relaxed text-gray-700">
							TalentProfile is laser-focused on technical hiring. By specializing, we can offer core
							job matching completely free for both developers and companies. We make money through
							optional premium features and services that companies choose when they need advanced
							capabilities. No forced subscriptions. No paying for features you don't need. Just
							smart matching that works.
						</p>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Final CTA -->
	<section
		class="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 px-4 py-20"
	>
		<!-- Background decorations -->
		<div class="absolute top-0 left-1/4 h-96 w-96 rounded-full bg-white/10 blur-3xl"></div>
		<div class="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-white/10 blur-3xl"></div>

		<div class="relative z-10 mx-auto max-w-4xl text-center">
			<div
				class="mb-8 inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm"
			>
				<Sparkles class="h-4 w-4" />
				<span>Ready to upgrade your hiring?</span>
			</div>

			<h2 class="mb-6 text-4xl leading-tight font-bold text-white md:text-5xl">
				Experience Developer-First Hiring
			</h2>
			<p class="mx-auto mb-10 max-w-2xl text-xl leading-relaxed text-white/90">
				Traditional ATS was built for HR workflows across all industries. TalentProfile was built
				exclusively for technical accuracy and developer experience. Try the platform designed
				specifically for tech hiring - completely free.
			</p>

			<div class="mb-10 flex flex-col justify-center gap-4 sm:flex-row">
				<a
					href="/"
					class="inline-flex items-center justify-center rounded-xl bg-white px-8 py-4 text-lg font-semibold text-blue-600 transition-all hover:bg-gray-50 hover:shadow-2xl"
				>
					For Developers: Upload CV
				</a>
				<a
					href="/drop-jd"
					class="inline-flex items-center justify-center rounded-xl border-2 border-white px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-white hover:text-blue-600"
				>
					For Companies: Post Job
				</a>
			</div>

			<div class="mx-auto flex max-w-fit flex-col gap-4 sm:flex-row">
				<div
					class="flex w-fit items-center justify-center gap-1 rounded-xl bg-white/10 p-1 px-3 backdrop-blur-sm"
				>
					<Gift class="h-5 w-5 text-white" />

					<span class="text-xs font-medium text-white">100% free forever</span>
				</div>
				<div
					class="flex w-fit items-center justify-center gap-1 rounded-xl bg-white/10 p-1 px-3 backdrop-blur-sm"
				>
					<Shield class="h-5 w-5 text-white" />
					<span class="text-xs font-medium text-white">Tech stack verification</span>
				</div>
				<div
					class="flex w-fit items-center justify-center gap-1 rounded-xl bg-white/10 p-1 px-3 backdrop-blur-sm"
				>
					<BarChart3 class="h-5 w-5 text-white" />
					<span class="text-xs font-medium text-white">Code quality analysis</span>
				</div>
			</div>
		</div>
	</section>
</div>
